import { useDashboard } from '../../hooks/useDashboard';
import { useCache } from '../../hooks/useCache';
import { fetchFootfall, fetchSOP } from '../../api';
import styles from './TabKpiRow.module.css';

function KpiCard({ label, value, unit, meta, metaType }) {
  return (
    <div className={styles.card}>
      <div className={styles.lbl}>{label}</div>
      <div className={styles.val}>{value}{unit && <span className={styles.unit}> {unit}</span>}</div>
      {meta && <div className={styles.meta}>
        <span className={`${styles.badge} ${metaType === 'down' ? styles.down : metaType === 'warn' ? styles.warn : styles.up}`}>{meta}</span>
      </div>}
    </div>
  );
}

function fmtHour(h) {
  const hh = Math.floor(h), mm = Math.round((h - hh) * 60);
  const ampm = hh >= 12 ? 'PM' : 'AM';
  const hd = hh > 12 ? hh - 12 : hh === 0 ? 12 : hh;
  return `${hd}:${String(mm).padStart(2, '0')} ${ampm}`;
}

export default function TabKpiRow({ tab }) {
  const { currentStore, datesParam, startDate, endDate } = useDashboard();

  const storeId = currentStore?.storeId;

  // All hooks called unconditionally at the top
  const ffKey = storeId ? `tabkpi-ff:${storeId}:${datesParam}` : null;
  const sopKey = storeId ? `tabkpi-sop:${storeId}:${startDate}:${endDate}` : null;
  const { data: ffData } = useCache(ffKey, () => fetchFootfall(storeId, datesParam));
  const { data: sopData } = useCache(sopKey, () => fetchSOP(storeId, startDate, endDate));

  if (!currentStore) return null;

  const footfall = ffData?.totalFootfall || 0;
  const dwell = ffData?.avgDwellTime || 0;
  const hyg = sopData?.hygiene?.totalBreaches || 0;
  const hygAvgDuration = sopData?.hygiene?.avgDirtyDurationMin || 0;
  const ppe = sopData?.ppe?.totalNonCompliant || 0;
  const total = hyg + ppe;

  if (tab === 'footfall') {
    const parseHour = t => { const [h, m] = t.split(':').map(Number); return h + (m || 0) / 60; };
    const openTime  = currentStore.open  ? fmtHour(parseHour(currentStore.open))  : '--';
    const closeTime = currentStore.close ? fmtHour(parseHour(currentStore.close)) : '--';
    const downtime  = ffData?.avgDowntime ?? null;
    const downtimeBadge = downtime == null ? 'neutral' : downtime < 15 ? 'up' : downtime < 30 ? 'warn' : 'down';
    const downtimeMeta  = downtime == null ? 'no data' : downtime < 15 ? 'Low' : downtime < 30 ? 'Moderate' : 'High';
    return (
      <div className={styles.row} style={{ gridTemplateColumns: 'repeat(4,minmax(0,1fr))' }}>
        <KpiCard label="Total Footfall" value={footfall.toLocaleString()} meta="this period" />
        <KpiCard label="Avg Dwell Time" value={dwell} unit="min" meta="avg for this store" />
        <KpiCard label="Avg Downtime" value={downtime != null ? `${downtime} min` : '—'} meta={downtimeMeta} metaType={downtimeBadge} />
        <div className={styles.card}>
          <div className={styles.lbl}>Store Operations</div>
          <div className={styles.opsRow}>
            <div className={styles.opsItem}><div className={styles.opsVal}>{openTime}</div><div className={styles.opsLbl}>Open</div></div>
            <div className={styles.opsSep}>·</div>
            <div className={styles.opsItem}><div className={styles.opsVal}>{closeTime}</div><div className={styles.opsLbl}>Close</div></div>
          </div>
        </div>
      </div>
    );
  }

  if (tab === 'compliance') {
    return (
      <div className={styles.row} style={{ gridTemplateColumns: 'repeat(3,minmax(0,1fr))' }}>
        <KpiCard label="Total SOP Breaches" value={total} meta="this period" metaType="down" />
        <KpiCard label="Hygiene Breaches" value={hygAvgDuration} unit="min" meta="avg duration" metaType="down" />
        <KpiCard label="PPE Breaches" value={ppe} meta={`${total > 0 ? Math.round((ppe / total) * 100) : 0}% of total`} metaType="down" />
      </div>
    );
  }

  return null;
}
